import {MutationFunction, useMutation, UseMutationOptions, UseMutationResult} from '@tanstack/react-query';

/**
 * A hook that synchronously executes a mutation, does not execute if the mutation is already pending.
 * 
 * @param mutationFn - The mutation function to execute.
 * @param options - The options for the mutation.
 * @returns The mutation results.
 */
export const useSyncMutation = <TData = unknown, TError = unknown, TVariables = void, TContext = unknown>({
  mutationKey,
  mutationFn,
  options
}: {
  mutationKey: string[];
  mutationFn: MutationFunction<TData, TVariables>;
  options?: Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationKey'>;
}): UseMutationResult<TData, TError, TVariables, TContext> => {
  const mutationResults = useMutation<TData, TError, TVariables, TContext>({
    mutationKey,
    mutationFn,
    ...options
  });

  return {
    ...mutationResults,
    mutate: (...params: Parameters<typeof mutationResults.mutate>) => {
      if (!mutationResults.isPending) {
        mutationResults.mutate(...params);
      }
    }
  };
};
